import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiFieldRole } from '@euler/model/bi/result';
import { Combine, X } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiButton } from '../../../components/ui/BiButton';
import { QuerySections } from '../QuerySections';
import {
  biEditorStartBlend,
  biEditorStopBlend,
  biEditorWithSecondDataset,
} from '../functions/biEditorBlend';
import { BiEditorState } from '../functions/biEditorState';
import { useBiDatasetCatalog } from '../functions/useBiDatasetCatalog';
import { BlendJoinSection } from './BlendJoinSection';
import { BlendKeysSection } from './BlendKeysSection';

/**
 * **Combine with…**: a second dataset, the keys both line up on and how
 * rows merge, then the second query's own measures, time range, filters,
 * sort and limit.
 */
export const BlendSection = memo(
  ({
    state,
    first,
    builtin,
    uploaded,
    onChange,
    onRetryUploads,
  }: {
    state: BiEditorState;
    /** The first query's catalog. */
    first: BiDatasetCatalog | null;
    builtin: BiDatasetCatalog[];
    uploaded: BiUploadedDatasetView[] | null;
    onChange: (next: BiEditorState) => void;
    onRetryUploads: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { blend } = state;
    const { state: second, retry } = useBiDatasetCatalog(
      blend?.second.dataset ?? null,
      { kind: 'ready', builtin, uploaded },
    );
    if (!blend) {
      const keyed = !!first?.fields.some(
        field => field.role === BiFieldRole.Dimension && field.conformedKey,
      );
      return (
        <View style={{ gap: rpx(6), alignItems: 'flex-start' }}>
          <BiButton
            icon={Combine}
            title="组合其他数据…"
            disabled={!keyed}
            onPress={() => onChange(biEditorStartBlend(state))}
          />
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {first && !keyed
              ? '这份数据没有可关联的字段（如日期、车牌、技师）'
              : '和另一份数据按日期、车牌等对齐，例如目标对比实际'}
          </Typography.Text>
        </View>
      );
    }
    const secondCatalog = second.kind === 'ready' ? second.catalog : null;
    return (
      <View
        style={{
          gap: rpx(18),
          padding: rpx(14),
          borderRadius: rpx(12),
          borderWidth: 1,
          borderColor: tint(aiColors.primary, 0.3),
          backgroundColor: tint(aiColors.primary, 0.03),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
            组合数据
          </Typography.Text>
          <BiButton
            icon={X}
            title="取消组合"
            tone="quiet"
            onPress={() => onChange(biEditorStopBlend(state))}
          />
        </View>
        <QuerySections
          query={blend.second}
          catalog={second}
          builtin={builtin}
          uploaded={uploaded}
          datasetTitle="第二份数据"
          datasetNote={first ? `和「${biText(first.label)}」组合` : undefined}
          exclude={state.dataset}
          withDimensions={false}
          onPick={dataset =>
            onChange(biEditorWithSecondDataset(state, blend, dataset))
          }
          onChange={query =>
            onChange({ ...state, blend: { ...blend, second: query } })
          }
          onRetryUploads={onRetryUploads}
          onRetryCatalog={retry}
        />
        {first && secondCatalog ? (
          <BlendKeysSection
            state={state}
            blend={blend}
            catalogs={[first, secondCatalog]}
            onChange={onChange}
          />
        ) : null}
        {blend.second.dataset ? (
          <BlendJoinSection
            join={blend.join}
            onChange={join => onChange({ ...state, blend: { ...blend, join } })}
          />
        ) : null}
      </View>
    );
  },
);
